<template>
  <div class="c-kdo-approvals">
    <n-loader :loading="$var('loadApprovals')" />

    <div class="kdo-toolbar">
      <div class="kdo-hint grow">
        Маршрут согласования — кто и в каком порядке подписывает документ. Шаги с одинаковым этапом выполняются
        параллельно, следующий этап начинается, когда на текущем всё подписано.
      </div>
      <button type="button" class="kdo-btn primary" @click="openEditor(null)">+ Добавить маршрут</button>
    </div>

    <div class="kdo-card">
      <table>
        <thead>
          <tr>
            <th>Название</th>
            <th>Тип документа</th>
            <th>Шаги</th>
            <th>Шаблонов</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rows" :key="item.id">
            <td>{{ item.title }}</td>
            <td><span class="kdo-badge" :class="item.type === 'custom' ? 'muted' : 'ok'">{{ typeTitle(item.type) }}</span></td>
            <td>
              <div v-for="step in item.steps" :key="step.id" class="step-line" :class="{ off: !step.isActive }">
                {{ step.stage + 1 }}. {{ step.title }} — {{ stepTypeTitle(step.approvalType) }}
                <span v-if="step.signerName">({{ step.signerName }})</span>
                <span class="kdo-badge" :class="step.requireNCA ? 'ok' : 'muted'">{{ step.requireNCA ? 'ЭЦП' : 'согласование' }}</span>
              </div>
            </td>
            <td>{{ item.documentTemplatesCount }}</td>
            <td class="actions">
              <button type="button" class="kdo-btn small" @click="openEditor(item)">Изменить</button>
              <button type="button" class="kdo-btn small danger" @click="toDelete = item">Удалить</button>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="5" class="empty">Маршрутов пока нет</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="form" class="kdo-overlay" @mousedown.self="closeEditor">
      <div class="kdo-dialog wide">
        <h3>{{ form.id ? 'Изменить маршрут' : 'Новый маршрут согласования' }}</h3>
        <div class="kdo-hint">Изменения не затрагивают уже созданные документы — у них свой снимок маршрута.</div>

        <div class="kdo-form">
          <div class="row">
            <div>
              <label class="caption">Название</label>
              <input v-model="form.title" type="text" placeholder="Подписание приказа об отпуске">
            </div>
            <div>
              <label class="caption">Тип документа</label>
              <select v-model="form.type">
                <option v-for="type in dict.approvalTypes" :key="type.key" :value="type.key">{{ type.title }}</option>
              </select>
              <div class="note">Тип определяет интеграцию с Енбек. Для обычных документов выбирайте «Обычный документ».</div>
            </div>
          </div>

          <div class="section-title">Шаги</div>
          <div v-for="(step, index) in form.steps" :key="index" class="step-card">
            <div class="row">
              <div class="stage">
                <label class="caption">Этап</label>
                <input v-model.number="step.stage" type="number" min="0" max="50">
              </div>
              <div>
                <label class="caption">Название шага</label>
                <input v-model="step.title" type="text" placeholder="Подписание руководителем">
              </div>
            </div>
            <div class="row">
              <div>
                <label class="caption">Кто выполняет</label>
                <select v-model="step.approvalType">
                  <option v-for="type in dict.stepTypes" :key="type.key" :value="type.key">{{ type.title }}</option>
                </select>
              </div>
              <div v-if="step.approvalType === 'exactUser'">
                <label class="caption">Сотрудник</label>
                <select v-model="step.signerId">
                  <option :value="null">— выберите —</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">{{ user.fullName }}</option>
                </select>
              </div>
            </div>
            <div class="step-bottom">
              <label class="check"><input v-model="step.requireNCA" type="checkbox"> Требуется подпись ЭЦП</label>
              <label class="check"><input v-model="step.isActive" type="checkbox"> Шаг активен</label>
              <span class="spacer"></span>
              <button type="button" class="icon-btn" :disabled="index === 0" @click="moveStep(index, -1)">↑</button>
              <button type="button" class="icon-btn" :disabled="index === form.steps.length - 1" @click="moveStep(index, 1)">↓</button>
              <button type="button" class="icon-btn danger" :disabled="form.steps.length === 1" @click="form.steps.splice(index, 1)">✕</button>
            </div>
          </div>
          <button type="button" class="kdo-btn small" @click="addStep">+ Добавить шаг</button>

          <div v-if="error" class="error">{{ error }}</div>
        </div>

        <div class="kdo-actions">
          <button type="button" class="kdo-btn" :disabled="saving" @click="closeEditor">Отмена</button>
          <button type="button" class="kdo-btn primary" :disabled="saving" @click="save">{{ saving ? 'Сохраняем...' : 'Сохранить' }}</button>
        </div>
      </div>
    </div>

    <c-modal-message
      v-if="toDelete"
      :title="`Удалить маршрут «${toDelete.title}»?`"
      description="Маршрут, который используется в шаблонах документов, удалить не получится."
      @agree="remove"
      @cancel="toDelete = null" />
  </div>
</template>

<script>
export default {
  name: 'CKdoApprovalTemplates',
  props: {
    companyId: { type: [ Number, String, ], required: true, },
  },
  data() {
    return {
      rows: [],
      dict: { approvalTypes: [], stepTypes: [], },
      users: [],
      form: null,
      saving: false,
      error: '',
      toDelete: null,
    }
  },
  created() {
    $api.kdo.dictionaries().then((response) => {
      this.dict = response.data.content
    })
    $api.kdo.users(this.companyId).then((response) => {
      this.users = response.data.content || []
    })
    this.load()
  },
  methods: {
    load() {
      this.$var('loadApprovals', true)
      $api.kdo.approvalTemplates(this.companyId).then((response) => {
        this.rows = response.data.content || []
      }).finally(() => {
        this.$var('loadApprovals', false)
      })
    },
    typeTitle(key) {
      const item = $n.find(this.dict.approvalTypes, (type) => type.key === key)
      return item ? item.title.replace(/ \(.*\)$/, '') : key
    },
    stepTypeTitle(key) {
      const item = $n.find(this.dict.stepTypes, (type) => type.key === key)
      return item ? item.title : key
    },
    emptyStep(stage) {
      return { id: null, title: '', description: '', stage, approvalType: 'exactUser', signerId: null, requireNCA: true, isActive: true, }
    },
    openEditor(item) {
      this.error = ''
      if (item) {
        this.form = {
          id: item.id,
          title: item.title,
          type: item.type,
          steps: item.steps.map((step) => ({ ...step, })),
        }
      } else {
        this.form = { id: null, title: '', type: 'custom', steps: [ this.emptyStep(0), ], }
      }
    },
    closeEditor() {
      if (this.saving) return
      this.form = null
    },
    addStep() {
      const stages = this.form.steps.map((step) => Number(step.stage) || 0)
      this.form.steps.push(this.emptyStep(stages.length ? Math.max(...stages) + 1 : 0))
    },
    moveStep(index, shift) {
      const steps = this.form.steps
      const target = index + shift
      if (target < 0 || target >= steps.length) return
      const [ moved, ] = steps.splice(index, 1)
      steps.splice(target, 0, moved)
    },
    save() {
      this.error = ''
      this.saving = true
      const payload = {
        title: this.form.title,
        type: this.form.type,
        steps: this.form.steps.map((step) => ({
          id: step.id,
          title: step.title,
          description: step.description,
          stage: step.stage,
          approvalType: step.approvalType,
          signerId: step.approvalType === 'exactUser' ? step.signerId : null,
          requireNCA: step.requireNCA,
          isActive: step.isActive,
        })),
      }
      const request = this.form.id ?
        $api.kdo.editApprovalTemplate(this.form.id, payload) :
        $api.kdo.createApprovalTemplate(this.companyId, payload)
      request.then(() => {
        this.form = null
        this.load()
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось сохранить маршрут'
      }).finally(() => {
        this.saving = false
      })
    },
    remove() {
      const item = this.toDelete
      this.toDelete = null
      this.$var('loadApprovals', true)
      $api.kdo.deleteApprovalTemplate(item.id).then(() => {
        this.load()
      }).catch(() => {
        this.$var('loadApprovals', false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './kdo.scss';

.grow {
  flex: 1;
  margin: 0;
}

.step-line {
  font-size: 13px;
  margin-bottom: 4px;

  &.off {
    opacity: 0.5;
    text-decoration: line-through;
  }
}

.actions {
  white-space: nowrap;
  text-align: right;

  .kdo-btn {
    margin-left: 6px;
  }
}

.step-card {
  border: 1px solid #ece4fb;
  border-radius: 14px;
  background: #faf8ff;
  padding: 14px 14px 4px;
  margin-bottom: 12px;

  .row:first-child {
    grid-template-columns: 110px 1fr;
  }

  .step-bottom {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 4px 0 10px;

    .spacer {
      flex: 1;
    }
  }
}
</style>
